/**
 * Client-side wrappers around the magic-link Pages Functions.
 *
 * Endpoints:
 *   POST /api/auth/magic-link  — request a magic-link
 *   GET  /api/auth/verify      — consume + verify a token
 */

export interface IssueResult {
  ok: true;
  /** Present only in dev (log-only mode). Production callers must wait for the email. */
  devVerifyUrl?: string;
}

export interface VerifyResult {
  ok: true;
  email: string;
  anonymousUserId: string;
  remoteId: string;
}

export interface ApiError {
  ok: false;
  error: string;
  status: number;
}

/**
 * The privacy-notice version the user consents to at signup.
 * Mirrors functions/_pdpa.ts CONSENT_VERSION. The policy this refers
 * to is not yet published (a launch blocker); this is the engineering
 * hook that records WHICH version was agreed to.
 */
export const CONSENT_VERSION = '2026-05-21-draft';

export async function requestMagicLink(
  email: string,
  anonymousUserId: string,
  consentVersion: string,
): Promise<IssueResult | ApiError> {
  let res: Response;
  try {
    res = await fetch('/api/auth/magic-link', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, anonymousUserId, consentVersion }),
    });
  } catch {
    return {
      ok: false,
      error: 'Could not reach the server. Are you offline?',
      status: 0,
    };
  }
  return parseResponse<IssueResult>(res);
}

/** Trigger a download of the user's complete data (PDPA right to access). */
export async function downloadMyData(): Promise<ApiError | null> {
  let res: Response;
  try {
    res = await fetch('/api/export', { method: 'GET' });
  } catch {
    return { ok: false, error: 'Could not reach the server.', status: 0 };
  }
  if (!res.ok) {
    return {
      ok: false,
      error: `Export failed (HTTP ${res.status}).`,
      status: res.status,
    };
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'propdefect-my-data.json';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return null;
}

/** Permanently delete the account + all server data (PDPA right to erasure). */
export async function deleteMyAccount(): Promise<ApiError | null> {
  let res: Response;
  try {
    res = await fetch('/api/account', { method: 'DELETE' });
  } catch {
    return { ok: false, error: 'Could not reach the server.', status: 0 };
  }
  if (!res.ok) {
    return {
      ok: false,
      error: `Delete failed (HTTP ${res.status}).`,
      status: res.status,
    };
  }
  return null;
}

/**
 * End the server session (sign out). Resolves even on network failure so
 * the caller can always drop the local session and reset to anonymous.
 */
export async function signOut(): Promise<void> {
  try {
    await fetch('/api/auth/signout', { method: 'POST' });
  } catch {
    /* ignore — the caller resets local state regardless */
  }
}

export async function verifyMagicLink(
  token: string,
): Promise<VerifyResult | ApiError> {
  let res: Response;
  try {
    res = await fetch(`/api/auth/verify?token=${encodeURIComponent(token)}`, {
      method: 'GET',
    });
  } catch {
    return {
      ok: false,
      error: 'Could not reach the server. Are you offline?',
      status: 0,
    };
  }
  return parseResponse<VerifyResult>(res);
}

async function parseResponse<T extends { ok: true }>(
  res: Response,
): Promise<T | ApiError> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return {
      ok: false,
      error: `Server returned an unexpected response (HTTP ${res.status}).`,
      status: res.status,
    };
  }
  if (
    typeof body === 'object' &&
    body !== null &&
    'ok' in body &&
    body.ok === true
  ) {
    return body as T;
  }
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  ) {
    return {
      ok: false,
      error: (body as { error: string }).error,
      status: res.status,
    };
  }
  return {
    ok: false,
    error: `Server error (HTTP ${res.status}).`,
    status: res.status,
  };
}
